/* assets/event-list.js
 * Events page: localised dates, country / type filters, an accordion list
 * with one shared small map, an overview map and calendar exports.
 * No dependencies. Google Maps is only loaded when a map is about to be shown.
 *
 * Times: Liquid renders event times in the shop's time zone, which is the
 * wall-clock time the admin typed in. They are treated as local time at the
 * event's location (country -> time zone below) for calendar exports.
 */
(function () {
  'use strict';

  var MAPS_PROMISE = null;

  function loadMapsApi(key) {
    if (window.google && window.google.maps && window.google.maps.importLibrary) {
      return Promise.resolve();
    }
    if (MAPS_PROMISE) return MAPS_PROMISE;
    MAPS_PROMISE = new Promise(function (resolve, reject) {
      var cb = '__eventMapsReady';
      window[cb] = function () { resolve(); };
      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
        '&v=weekly&libraries=marker&loading=async&callback=' + cb;
      script.async = true;
      script.onerror = function () { reject(new Error('Google Maps API failed to load')); };
      document.head.appendChild(script);
    });
    return MAPS_PROMISE;
  }

  var COUNTRY_TZ = {
    FI: 'Europe/Helsinki', SE: 'Europe/Stockholm', NO: 'Europe/Oslo', DK: 'Europe/Copenhagen',
    IS: 'Atlantic/Reykjavik', EE: 'Europe/Tallinn', LV: 'Europe/Riga', LT: 'Europe/Vilnius',
    DE: 'Europe/Berlin', NL: 'Europe/Amsterdam', BE: 'Europe/Brussels', LU: 'Europe/Luxembourg',
    FR: 'Europe/Paris', ES: 'Europe/Madrid', PT: 'Europe/Lisbon', IT: 'Europe/Rome',
    CH: 'Europe/Zurich', AT: 'Europe/Vienna', PL: 'Europe/Warsaw', CZ: 'Europe/Prague',
    GB: 'Europe/London', IE: 'Europe/Dublin'
  };

  var PIN_SVG =
    '<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">' +
    '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20c0-6.6-5.4-12-12-12z" fill="currentColor"/>' +
    '<circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>';

  /* ---------- Date helpers (wall-clock time, formatted in "UTC") ---------- */

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // "2026-10-03T10:00" -> ms timestamp whose UTC fields equal the wall time
  function wallMs(str) {
    var m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(str || '');
    if (!m) return null;
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  }

  function ymd(ms) {
    var d = new Date(ms);
    return d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate());
  }

  function ymdhms(ms) {
    var d = new Date(ms);
    return ymd(ms) + 'T' + pad(d.getUTCHours()) + pad(d.getUTCMinutes()) + '00';
  }

  // Offset (ms) of a time zone at a given instant
  function tzOffset(ms, tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(new Date(ms));
      var p = {};
      parts.forEach(function (x) { p[x.type] = x.value; });
      var asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
      return asUtc - ms;
    } catch (e) {
      return 0;
    }
  }

  // Wall time in a time zone -> real UTC instant
  function zonedToUtc(wall, tz) {
    var guess = wall - tzOffset(wall, tz);
    return wall - tzOffset(guess, tz);
  }

  function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  function Formatter(locale) {
    var base = { timeZone: 'UTC' };
    function f(opts) {
      try { return new Intl.DateTimeFormat(locale, Object.assign({}, base, opts)); }
      catch (e) { return new Intl.DateTimeFormat('en-GB', Object.assign({}, base, opts)); }
    }
    this.day = f({ day: 'numeric' });
    this.monthShort = f({ month: 'short' });
    this.monthYear = f({ month: 'long', year: 'numeric' });
    this.weekdayShort = f({ weekday: 'short' });
    this.weekdayLong = f({ weekday: 'long' });
    this.time = f({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    this.dayMonth = f({ day: 'numeric', month: 'short' });
    this.full = f({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    this.rangeDate = f({ day: 'numeric', month: 'long', year: 'numeric' });
    this.rangeDateTime = f({ day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  }

  Formatter.prototype.range = function (fmt, a, b) {
    if (fmt.formatRange) {
      try { return fmt.formatRange(new Date(a), new Date(b)); } catch (e) { /* fall through */ }
    }
    return fmt.format(new Date(a)) + ' – ' + fmt.format(new Date(b));
  };

  Formatter.prototype.clean = function (s) {
    // Some locales put a trailing dot on abbreviations ("lok.") — keep it short
    return s.replace(/\.$/, '');
  };

  /* ---------- Main ---------- */

  function EventList(root) {
    var self = this;
    this.root = root;
    this.apiKey = root.dataset.apiKey || '';
    this.mapId = root.dataset.mapId || 'DEMO_MAP_ID';
    this.center = {
      lat: parseFloat(root.dataset.centerLat) || 62,
      lng: parseFloat(root.dataset.centerLng) || 20
    };
    this.zoom = parseInt(root.dataset.zoom, 10) || 4;
    this.miniZoom = parseInt(root.dataset.miniZoom, 10) || 13;
    this.defaultTz = root.dataset.defaultTz || 'Europe/Helsinki';
    this.countOne = root.dataset.countOne || '1 upcoming event';
    this.countMany = root.dataset.countMany || '[count] upcoming events';
    this.emptyText = root.dataset.emptyText || '';

    var lang = (document.documentElement.lang || 'en').toLowerCase();
    this.locale = lang === 'en' ? 'en-GB' : lang;
    this.fmt = new Formatter(this.locale);

    this.groupsEl = root.querySelector('[data-event-groups]');
    this.mapEl = root.querySelector('[data-event-map]');
    this.countEl = root.querySelector('[data-event-count]');
    this.noResultsEl = root.querySelector('[data-event-no-results]');
    this.countryEl = root.querySelector('[data-event-country]');
    this.filterEls = Array.prototype.slice.call(root.querySelectorAll('[data-event-filter]'));
    this.groups = Array.prototype.slice.call(root.querySelectorAll('[data-event-group]'));

    this.activeCountry = '';
    this.activeType = '';
    this.map = null;
    this.miniMap = null;
    this.miniMarker = null;
    this.miniEl = null;
    this.openItem = null;

    var now = Date.now() / 1000;

    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-event]')).map(function (el) {
      var lat = parseFloat(el.dataset.lat);
      var lng = parseFloat(el.dataset.lng);
      var country = (el.dataset.country || '').toUpperCase();
      return {
        el: el,
        details: el.querySelector('details'),
        id: el.id,
        name: el.dataset.name || '',
        lat: isFinite(lat) ? lat : null,
        lng: isFinite(lng) ? lng : null,
        country: country,
        type: el.dataset.type || '',
        start: wallMs(el.dataset.start),
        end: wallMs(el.dataset.end),
        startTs: parseInt(el.dataset.startTs, 10) || 0,
        endTs: parseInt(el.dataset.endTs, 10) || 0,
        allDay: el.dataset.allDay === 'true',
        hasEnd: el.dataset.hasEnd === 'true',
        location: el.dataset.location || '',
        url: el.dataset.url || '',
        tz: COUNTRY_TZ[country] || self.defaultTz,
        marker: null,
        visible: true
      };
    });

    // The page may be served from cache: drop events that ended since render.
    this.items = this.items.filter(function (item) {
      if (item.endTs && item.endTs < now) { item.el.remove(); return false; }
      return true;
    });

    this.items.forEach(function (item) {
      if (item.start === null) return;
      item.multiDay = item.end !== null && ymd(item.start) !== ymd(item.end);
      var live = item.el.querySelector('[data-event-live]');
      if (live && item.startTs <= now) live.hidden = false;
      self.localizeDates(item);
      self.setupCalendar(item);
    });

    this.localizeMonths();
    this.localizeCountries();
    this.bindEvents();
    this.applyInitialFilters();

    if (!this.apiKey) {
      this.root.classList.add('event-list--no-maps');
    } else if (this.mapEl && this.items.some(function (i) { return i.lat !== null; })) {
      this.observeMap();
    } else if (this.mapEl) {
      this.root.classList.add('event-list--no-overview');
    }

    this.openFromHash();
  }

  /* ---------- Localisation ---------- */

  EventList.prototype.localizeDates = function (item) {
    var f = this.fmt;
    var s = item.start;
    var e = item.hasEnd ? item.end : s;
    var el = item.el;

    var badgeDay = el.querySelector('[data-event-badge-day]');
    var badgeMonth = el.querySelector('[data-event-badge-month]');
    var when = el.querySelector('[data-event-when]');
    var whenLong = el.querySelector('[data-event-when-long]');

    try {
      if (badgeDay) {
        badgeDay.textContent = item.multiDay
          ? f.day.format(new Date(s)) + '–' + f.day.format(new Date(e))
          : f.day.format(new Date(s));
      }
      if (badgeMonth) {
        var m1 = f.clean(f.monthShort.format(new Date(s)));
        var m2 = f.clean(f.monthShort.format(new Date(e)));
        badgeMonth.textContent = item.multiDay && m1 !== m2 ? m1 + '–' + m2 : m1;
      }

      var timeRange = f.time.format(new Date(s)) + (item.hasEnd ? '–' + f.time.format(new Date(e)) : '');

      if (when) {
        if (item.multiDay) {
          when.textContent = f.range(f.dayMonth, s, e);
        } else if (item.allDay) {
          when.textContent = capitalize(f.weekdayLong.format(new Date(s)));
        } else {
          when.textContent = capitalize(f.clean(f.weekdayShort.format(new Date(s)))) + ' ' + timeRange;
        }
      }

      if (whenLong) {
        if (item.multiDay) {
          whenLong.textContent = item.allDay
            ? f.range(f.rangeDate, s, e)
            : f.range(f.rangeDateTime, s, e);
        } else {
          whenLong.textContent = capitalize(f.full.format(new Date(s))) + (item.allDay ? '' : ', ' + timeRange);
        }
      }

      // Short label for the map pin
      item.pinLabel = item.multiDay ? f.range(f.dayMonth, s, e) : f.dayMonth.format(new Date(s));
    } catch (err) {
      item.pinLabel = badgeDay ? badgeDay.textContent : '';
    }
  };

  EventList.prototype.localizeMonths = function () {
    var f = this.fmt;
    this.root.querySelectorAll('[data-month]').forEach(function (h) {
      var m = /^(\d{4})-(\d{2})$/.exec(h.dataset.month || '');
      if (!m) return;
      try {
        h.textContent = capitalize(f.monthYear.format(new Date(Date.UTC(+m[1], +m[2] - 1, 15))));
      } catch (e) { /* keep server text */ }
    });
  };

  EventList.prototype.localizeCountries = function () {
    if (!window.Intl || !Intl.DisplayNames) return;
    var names;
    try { names = new Intl.DisplayNames([this.locale], { type: 'region' }); } catch (e) { return; }
    this.root.querySelectorAll('[data-country-code]').forEach(function (el) {
      var code = el.dataset.countryCode;
      // Only replace a bare code; names Shopify already localised stay as they are.
      if (code && el.textContent.trim().toUpperCase() === code) {
        try { el.textContent = names.of(code) || code; } catch (e) { /* ignore */ }
      }
    });
  };

  /* ---------- Calendar ---------- */

  EventList.prototype.setupCalendar = function (item) {
    var gcal = item.el.querySelector('[data-event-gcal]');
    var ics = item.el.querySelector('[data-event-ics]');
    var s = item.start;
    var e = item.hasEnd ? item.end : (item.allDay ? s : s + 2 * 3600 * 1000);

    if (gcal) {
      var dates = item.allDay
        ? ymd(s) + '/' + ymd(e + 86400000)
        : ymdhms(s) + '/' + ymdhms(e);
      var params = [
        'action=TEMPLATE',
        'text=' + encodeURIComponent(item.name),
        'dates=' + dates,
        'ctz=' + encodeURIComponent(item.tz),
        'location=' + encodeURIComponent(item.location),
        'details=' + encodeURIComponent(item.url || location.href.split('#')[0] + '#' + item.id)
      ];
      gcal.href = 'https://calendar.google.com/calendar/render?' + params.join('&');
      gcal.hidden = false;
    }

    if (ics && window.Blob && window.URL && URL.createObjectURL) {
      ics.hidden = false;
      ics.addEventListener('click', function () {
        var esc = function (t) {
          return (t || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
        };
        var stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
        var lines = [
          'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Swan Golf//Events//EN', 'CALSCALE:GREGORIAN',
          'BEGIN:VEVENT',
          'UID:' + item.id + '@' + location.hostname,
          'DTSTAMP:' + stamp
        ];
        if (item.allDay) {
          lines.push('DTSTART;VALUE=DATE:' + ymd(s));
          lines.push('DTEND;VALUE=DATE:' + ymd(e + 86400000));
        } else {
          lines.push('DTSTART:' + ymdhms(zonedToUtc(s, item.tz)) + 'Z');
          lines.push('DTEND:' + ymdhms(zonedToUtc(e, item.tz)) + 'Z');
        }
        lines.push('SUMMARY:' + esc(item.name));
        if (item.location) lines.push('LOCATION:' + esc(item.location));
        lines.push('URL:' + (item.url || location.href.split('#')[0] + '#' + item.id));
        lines.push('END:VEVENT', 'END:VCALENDAR');

        var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = item.id.replace(/^event-/, '') + '.ics';
        document.body.appendChild(a);
        a.click();
        setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      });
    }
  };

  /* ---------- Filters ---------- */

  EventList.prototype.bindEvents = function () {
    var self = this;

    if (this.countryEl) {
      this.countryEl.addEventListener('change', function () {
        self.activeCountry = self.countryEl.value;
        self.applyFilters(true);
      });
    }

    this.filterEls.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.setType(btn.dataset.eventFilter || '');
        self.applyFilters(true);
      });
    });

    this.items.forEach(function (item) {
      if (!item.details) return;
      item.details.addEventListener('toggle', function () {
        if (item.details.open) self.onOpen(item);
        else if (self.openItem === item) self.onClose(item);
      });
      item.el.addEventListener('mouseenter', function () { self.hoverMarker(item, true); });
      item.el.addEventListener('mouseleave', function () { self.hoverMarker(item, false); });
    });
  };

  EventList.prototype.setType = function (type) {
    this.activeType = type;
    this.filterEls.forEach(function (b) {
      var on = (b.dataset.eventFilter || '') === type;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  EventList.prototype.hasCountry = function (code) {
    return this.items.some(function (i) { return i.country === code; });
  };

  EventList.prototype.applyInitialFilters = function () {
    var params = new URLSearchParams(location.search);
    var country = (params.get('country') || '').toUpperCase();
    var type = params.get('type') || '';

    if (!country && this.root.dataset.visitorCountry) {
      country = this.root.dataset.visitorCountry.toUpperCase();
    }
    if (country && this.countryEl && this.hasCountry(country)) {
      this.activeCountry = country;
      this.countryEl.value = country;
    }
    if (type && this.filterEls.some(function (b) { return b.dataset.eventFilter === type; })) {
      this.setType(type);
    }
    this.applyFilters(false);
  };

  EventList.prototype.applyFilters = function (updateUrl) {
    var self = this;
    var visible = 0;

    this.items.forEach(function (item) {
      var show = (!self.activeCountry || item.country === self.activeCountry) &&
                 (!self.activeType || item.type === self.activeType);
      item.visible = show;
      item.el.hidden = !show;
      if (!show && item.details && item.details.open) item.details.open = false;
      if (item.marker) item.marker.map = show ? self.map : null;
      if (show) visible++;
    });

    this.groups.forEach(function (g) {
      g.hidden = !g.querySelector('[data-event]:not([hidden])');
    });

    if (this.noResultsEl) {
      this.noResultsEl.hidden = visible !== 0;
      if (visible === 0 && this.items.length === 0 && this.emptyText) {
        this.noResultsEl.textContent = this.emptyText;
      }
    }
    if (this.countEl) {
      this.countEl.textContent = visible === 1 ? this.countOne : this.countMany.replace('[count]', visible);
    }

    if (updateUrl && window.history && history.replaceState) {
      var params = new URLSearchParams(location.search);
      if (this.activeCountry) params.set('country', this.activeCountry); else params.delete('country');
      if (this.activeType) params.set('type', this.activeType); else params.delete('type');
      var qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
    }

    this.fitToVisible();
  };

  /* ---------- Accordion ---------- */

  EventList.prototype.onOpen = function (item) {
    var self = this;
    // Exclusive accordion for browsers without <details name> support
    this.items.forEach(function (i) {
      if (i !== item && i.details && i.details.open) i.details.open = false;
    });
    this.openItem = item;

    this.items.forEach(function (i) {
      i.el.classList.toggle('is-selected', i === item);
      if (i.marker && i.marker.content) {
        i.marker.content.classList.toggle('is-active', i === item);
        i.marker.zIndex = i === item ? 10 : 1;
      }
    });

    if (this.map && item.lat !== null) {
      this.map.panTo({ lat: item.lat, lng: item.lng });
    }

    this.attachMiniMap(item);

    if (window.history && history.replaceState) {
      history.replaceState(null, '', location.pathname + location.search + '#' + item.id);
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'event_open', { event_name: item.name });
    }
  };

  EventList.prototype.onClose = function (item) {
    this.openItem = null;
    item.el.classList.remove('is-selected');
    if (item.marker && item.marker.content) item.marker.content.classList.remove('is-active');
    if (window.history && history.replaceState && location.hash === '#' + item.id) {
      history.replaceState(null, '', location.pathname + location.search);
    }
  };

  EventList.prototype.open = function (item, scroll, focus) {
    if (!item || !item.details) return;
    if (!item.visible) {
      // Opened from a link while filtered out: reset filters first
      this.activeCountry = '';
      this.activeType = '';
      if (this.countryEl) this.countryEl.value = '';
      this.setType('');
      this.applyFilters(true);
    }
    item.details.open = true;
    if (scroll) {
      item.el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      var summary = item.details.querySelector('summary');
      if (focus && summary) summary.focus({ preventScroll: true });
    }
  };

  EventList.prototype.openFromHash = function () {
    var id = decodeURIComponent((location.hash || '').slice(1));
    if (!id) return;
    var item = this.items.filter(function (i) { return i.id === id; })[0];
    var self = this;
    if (item) setTimeout(function () { self.open(item, true); }, 60);
  };

  /* ---------- Maps ---------- */

  EventList.prototype.libs = function () {
    return loadMapsApi(this.apiKey).then(function () {
      return Promise.all([
        google.maps.importLibrary('maps'),
        google.maps.importLibrary('marker')
      ]);
    });
  };

  EventList.prototype.attachMiniMap = function (item) {
    var self = this;
    var slot = item.el.querySelector('[data-event-map-slot]');
    if (!slot || !this.apiKey || item.lat === null) return;

    if (!this.miniEl) {
      this.miniEl = document.createElement('div');
      this.miniEl.className = 'event-card__map';
    }
    slot.appendChild(this.miniEl);
    var pos = { lat: item.lat, lng: item.lng };

    if (this.miniMap) {
      this.miniMap.setCenter(pos);
      this.miniMap.setZoom(this.miniZoom);
      this.miniMarker.position = pos;
      return;
    }

    this.libs().then(function (libs) {
      var Map = libs[0].Map;
      var AdvancedMarkerElement = libs[1].AdvancedMarkerElement;
      var current = self.openItem || item;
      var p = { lat: current.lat, lng: current.lng };

      self.miniMap = new Map(self.miniEl, {
        center: p,
        zoom: self.miniZoom,
        mapId: self.mapId,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative'
      });
      var pin = document.createElement('div');
      pin.className = 'event-mini-pin';
      pin.innerHTML = PIN_SVG;
      self.miniMarker = new AdvancedMarkerElement({ map: self.miniMap, position: p, content: pin });
    }).catch(function () {
      self.root.classList.add('event-list--no-maps');
    });
  };

  EventList.prototype.observeMap = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) { this.initMap(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { io.disconnect(); self.initMap(); }
      });
    }, { rootMargin: '400px' });
    io.observe(this.mapEl);
  };

  EventList.prototype.initMap = function () {
    var self = this;
    if (this.mapEl.offsetParent === null) return; // hidden (e.g. on mobile)

    this.libs().then(function (libs) {
      var Map = libs[0].Map;
      var AdvancedMarkerElement = libs[1].AdvancedMarkerElement;

      self.map = new Map(self.mapEl, {
        center: self.center,
        zoom: self.zoom,
        mapId: self.mapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'cooperative'
      });

      self.items.forEach(function (item) {
        if (item.lat === null) return;
        var pin = document.createElement('div');
        pin.className = 'event-pin';
        pin.textContent = item.pinLabel || '';
        pin.setAttribute('role', 'img');
        pin.setAttribute('aria-label', item.name + (item.pinLabel ? ', ' + item.pinLabel : ''));

        item.marker = new AdvancedMarkerElement({
          map: item.visible ? self.map : null,
          position: { lat: item.lat, lng: item.lng },
          content: pin,
          gmpClickable: true
        });
        item.marker.addListener('click', function () { self.open(item, true, true); });
        if (self.openItem === item) pin.classList.add('is-active');
      });

      self.root.classList.add('event-list--map-ready');
      self.fitToVisible();
    }).catch(function () {
      self.root.classList.add('event-list--no-overview');
    });
  };

  EventList.prototype.hoverMarker = function (item, on) {
    if (item.marker && item.marker.content) {
      item.marker.content.classList.toggle('is-hover', on);
      item.marker.zIndex = on || this.openItem === item ? 10 : 1;
    }
  };

  EventList.prototype.fitToVisible = function () {
    if (!this.map) return;
    var pts = this.items.filter(function (i) { return i.visible && i.lat !== null; });
    if (!pts.length) return;
    if (pts.length === 1) {
      this.map.setCenter({ lat: pts[0].lat, lng: pts[0].lng });
      this.map.setZoom(10);
      return;
    }
    var bounds = new google.maps.LatLngBounds();
    pts.forEach(function (p) { bounds.extend({ lat: p.lat, lng: p.lng }); });
    this.map.fitBounds(bounds, 56);
  };

  /* ---------- Init ---------- */

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-event-list]').forEach(function (el) {
      if (el.dataset.eventInit === 'true') return;
      el.dataset.eventInit = 'true';
      new EventList(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();